import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { inject, Injectable } from '@angular/core';
import { VerifyEmailDialogComponent } from '@app/modules/dialogs/verify-email-dialog/verify-email-dialog.component';
import { InfoDialogComponent } from '@app/modules/dialogs/info-dialog/info-dialog.component';
import { UserInterface } from '@app/interfaces/user.interface';
import { UsersService } from '@app/services/users.service';
import { firstValueFrom, Observable, Subscription, take } from 'rxjs';
import { VerifyPhoneDialogComponent } from '@app/modules/dialogs/verify-phone-dialog/verify-phone-dialog.component';
import { CommunicationPreferencesDialogComponent } from '@app/modules/dialogs/communication-preferences-dialog/communication-preferences-dialog.component';
import { BlacklistDialogComponent } from '@app/modules/dialogs/blacklist-dialog/blacklist-dialog.component';
import { TooManyRequestsDialogComponent } from '@app/modules/dialogs/too-many-requests-dialog/too-many-requests-dialog.component';
import { ThrottleDialogComponent } from '@app/modules/dialogs/throttle-dialog/throttle-dialog.component';
import { LockoutDialogComponent } from '@app/modules/dialogs/lockout-dialog/lockout-dialog.component';
import { ConfirmDialogComponent } from '@app/modules/dialogs/confirm-dialog/confirm-dialog.component';
import { IncomeDialogComponent } from '@app/modules/dialogs/income-dialog/income-dialog.component';
import { ExpenditureDialogComponent } from '@app/modules/dialogs/expenditure-dialog/expenditure-dialog.component';
import { PropertyDialogComponent } from '@app/modules/dialogs/property-dialog/property-dialog.component';
import { HouseholdDialogComponent } from '@app/modules/dialogs/household-dialog/household-dialog.component';
import { BankAccountDialogComponent } from '@app/modules/dialogs/bank-account-dialog/bank-account-dialog.component';
import { UploadDialogComponent, UploadDialogData } from '@app/modules/dialogs/upload-dialog/upload-dialog.component';
import { AdminDialogComponent } from '@app/modules/dialogs/admin-dialog/admin-dialog.component';
import { DocumentsConfirmDialogComponent } from '@app/modules/dialogs/documents-confirm-dialog/documents-confirm-dialog.component';
import { ActivationDialogComponent } from '@app/modules/dialogs/activation-dialog/activation-dialog.component';
import { BankIdDialogComponent } from '@app/modules/dialogs/bankid-dialog/bankid-dialog.component';
import {
    CheckDataAfterRestoreDialogComponent,
    CheckDataAfterRestoreDialogResult,
} from '@app/modules/dialogs/check-data-after-restore-dialog/check-data-after-restore-dialog.component';
import { InvalidModelationsDialogComponent } from '@app/modules/dialogs/invalid-modelations-dialog/invalid-modelations-dialog.component';
import { RichVerifyDialogComponent } from '@app/modules/dialogs/rich-verify-phone-dialog/rich-verify-dialog.component';
import { ComponentType } from '@angular/cdk/portal';
import { MortgageInterface } from '@app/interfaces/mortgage.interface';

@Injectable({ providedIn: 'root' })
export class DialogService {
    private readonly usersService = inject(UsersService);
    private readonly dialog = inject(MatDialog);

    public defaultDialogConfig: MatDialogConfig = {
        disableClose: false,
    };
    user: UserInterface;
    user$: Subscription;

    constructor() {
        this.user$ = this.usersService.user$.subscribe((user: UserInterface) => {
            if (user) {
                this.user = user;
            } else {
                this.dialog.closeAll();
            }
        });
    }

    public verifyEmail(email: string, code: string = null): MatDialogRef<VerifyEmailDialogComponent> {
        const dialogConfig: MatDialogConfig = {
            ...this.defaultDialogConfig,
            disableClose: true,
            panelClass: 'new-simple-dialog',
            data: {
                email,
                code,
            },
        };
        return this.dialog.open(VerifyEmailDialogComponent, dialogConfig);
    }

    public verifyPhone(phone: string): MatDialogRef<VerifyPhoneDialogComponent> {
        const dialogConfig: MatDialogConfig = {
            ...this.defaultDialogConfig,
            disableClose: true,
            panelClass: 'new-simple-dialog',
            data: {
                phone,
            },
        };
        return this.dialog.open(VerifyPhoneDialogComponent, dialogConfig);
    }

    public showInfoTextDialog(data?: any, config?: MatDialogConfig): MatDialogRef<InfoDialogComponent> {
        const dialogConfig: MatDialogConfig = {
            ...this.defaultDialogConfig,
            ...config,
            panelClass: 'new-simple-dialog',
            data: {
                ...data,
            },
        };
        return this.dialog.open(InfoDialogComponent, dialogConfig);
    }

    public showCommunicationPreferencesDialog(
        config?: MatDialogConfig
    ): MatDialogRef<CommunicationPreferencesDialogComponent> {
        const dialogConfig: MatDialogConfig = {
            ...this.defaultDialogConfig,
            disableClose: true,
            ...config,
            panelClass: 'new-simple-dialog',
        };

        return this.dialog.open(CommunicationPreferencesDialogComponent, dialogConfig);
    }

    public showBlacklistDialog(config?: MatDialogConfig): MatDialogRef<BlacklistDialogComponent> {
        const dialogConfig: MatDialogConfig = {
            ...this.defaultDialogConfig,
            disableClose: true,
            ...config,
            panelClass: 'new-simple-dialog',
        };

        return this.dialog.open(BlacklistDialogComponent, dialogConfig);
    }

    public showTooManyRequestsDialog(config?: MatDialogConfig): MatDialogRef<TooManyRequestsDialogComponent> {
        const dialogConfig: MatDialogConfig = {
            ...this.defaultDialogConfig,
            panelClass: 'new-simple-dialog',
            disableClose: true,
            ...config,
        };

        return this.dialog.open(TooManyRequestsDialogComponent, dialogConfig);
    }

    async showThrottleDialog(phoneNumber: string, phoneCountryCode: string): Promise<boolean> {
        const dialogConfig: MatDialogConfig = {
            ...this.defaultDialogConfig,
            panelClass: 'new-simple-dialog',
            disableClose: true,
        };

        const dialogRef: MatDialogRef<ThrottleDialogComponent> = this.dialog.open(
            ThrottleDialogComponent,
            dialogConfig
        );
        dialogRef.componentInstance.data = { country: phoneCountryCode, phone: phoneNumber };
        return await firstValueFrom(dialogRef.afterClosed());
    }

    async showLockoutDialog(seconds: number): Promise<boolean> {
        const dialogConfig: MatDialogConfig = {
            ...this.defaultDialogConfig,
            panelClass: 'new-simple-dialog',
            disableClose: true,
        };

        const dialogRef: MatDialogRef<LockoutDialogComponent> = this.dialog.open(LockoutDialogComponent, dialogConfig);
        dialogRef.componentInstance.seconds = seconds;
        dialogRef.componentInstance.startCountdown();
        return await firstValueFrom(dialogRef.afterClosed());
    }

    public confirm(data: { title?: string; message?: string; okText?: string; cancelText?: string }): Observable<any> {
        const layerConfig: MatDialogConfig = {
            ...this.defaultDialogConfig,
            panelClass: 'new-simple-dialog',
            disableClose: true,
            data,
        };
        return this.openDialog<ConfirmDialogComponent>(ConfirmDialogComponent, layerConfig);
    }

    public income(data?: any): Observable<any> {
        const layerConfig: MatDialogConfig = {
            ...this.defaultDialogConfig,
            panelClass: 'new-modal-dialog',
            disableClose: true,
            data,
        };
        return this.openDialog<IncomeDialogComponent>(IncomeDialogComponent, layerConfig);
    }

    public expenditure(data?: any): Observable<any> {
        const layerConfig: MatDialogConfig = {
            ...this.defaultDialogConfig,
            disableClose: true,
            data,
            panelClass: 'new-modal-dialog',
        };
        return this.openDialog<ExpenditureDialogComponent>(ExpenditureDialogComponent, layerConfig);
    }

    public property(data?: any): Observable<any> {
        const layerConfig: MatDialogConfig = {
            ...this.defaultDialogConfig,
            disableClose: true,
            data,
            panelClass: 'new-modal-dialog',
        };
        return this.openDialog<PropertyDialogComponent>(PropertyDialogComponent, layerConfig);
    }

    public household(data?: any): Observable<any> {
        const layerConfig: MatDialogConfig = {
            ...this.defaultDialogConfig,
            disableClose: true,
            data,
            panelClass: 'new-modal-dialog',
        };
        return this.openDialog<HouseholdDialogComponent>(HouseholdDialogComponent, layerConfig);
    }

    public bankAccount(data?: any): Observable<any> {
        const layerConfig: MatDialogConfig = {
            ...this.defaultDialogConfig,
            disableClose: true,
            data,
            panelClass: 'new-modal-dialog',
        };

        return this.openDialog<BankAccountDialogComponent>(BankAccountDialogComponent, layerConfig);
    }

    public upload(data?: UploadDialogData): Observable<any> {
        const layerConfig: MatDialogConfig = {
            ...this.defaultDialogConfig,
            disableClose: true,
            panelClass: 'new-modal-dialog',
            data,
        };
        return this.openDialog<UploadDialogComponent>(UploadDialogComponent, layerConfig);
    }

    public admin(data?: any): Observable<any> {
        const layerConfig: MatDialogConfig = {
            ...this.defaultDialogConfig,
            disableClose: true,
            data,
            panelClass: 'new-simple-dialog',
        };
        return this.openDialog<AdminDialogComponent>(AdminDialogComponent, layerConfig);
    }

    public documentsConfirm(data?: any): Observable<any> {
        const layerConfig: MatDialogConfig = {
            width: '900px',
            ...this.defaultDialogConfig,
            data,
            panelClass: 'new-simple-dialog',
        };
        return this.openDialog<DocumentsConfirmDialogComponent>(DocumentsConfirmDialogComponent, layerConfig);
    }

    public activate(config?: any): Observable<any> {
        const layerConfig: MatDialogConfig = {
            maxWidth: '400px',
            disableClose: true,
            ...this.defaultDialogConfig,
            ...config,
            panelClass: 'new-simple-dialog',
        };
        return this.openDialog<ActivationDialogComponent>(ActivationDialogComponent, layerConfig);
    }

    public bankId(data?: any): Observable<any> {
        const layerConfig: MatDialogConfig = {
            ...this.defaultDialogConfig,
            data,
            panelClass: 'new-simple-dialog',
        };
        return this.openDialog<BankIdDialogComponent>(BankIdDialogComponent, layerConfig);
    }

    public restoreDealDialog(mortgage: MortgageInterface): Observable<CheckDataAfterRestoreDialogResult> {
        return this.dialog
            .open<CheckDataAfterRestoreDialogComponent, MortgageInterface, CheckDataAfterRestoreDialogResult>(
                CheckDataAfterRestoreDialogComponent,
                {
                    ...this.defaultDialogConfig,
                    data: mortgage,
                    width: '600px',
                }
            )
            .afterClosed()
            .pipe(take(1));
    }

    public invalidModelationsDialog(mortgage: MortgageInterface): Observable<any> {
        return this.dialog
            .open<InvalidModelationsDialogComponent, MortgageInterface, never>(InvalidModelationsDialogComponent, {
                ...this.defaultDialogConfig,
                data: mortgage,
                width: '800px',
            })
            .afterClosed()
            .pipe(take(1));
    }

    public richVerify(): MatDialogRef<RichVerifyDialogComponent> {
        return this.dialog.open<RichVerifyDialogComponent>(RichVerifyDialogComponent, {
            closeOnNavigation: false,
            disableClose: true,
            width: '400px',
            panelClass: 'new-simple-dialog',
        });
    }

    private openDialog<C>(component: ComponentType<C>, config?: any): Observable<any> {
        return this.dialog.open<C>(component, config).afterClosed().pipe(take(1));
    }
}
